import { useI18n } from "hooks/i18n/i18n";
import { getTicketClass } from "pages/AccountSettlementHistoryOrderId/utils/getTicketClass";
import { useMemo } from "react";

import { PRODUCT_TYPE } from "../../constants/ProductType";
import { getProductType } from "../../utils/getProductType";
import { getReleaseDate } from "../../utils/getReleaseDate";
import { getTicketStatus } from "../../utils/getTicketStatus";

import type { ProductItem } from "../../types/ProductItem";
import type { TimeStamp } from "../../types/TimeStamp";
import type { Store } from "store";

type EventPart = {
  name: string;
  openTime: TimeStamp;
};

type UseOrderListInput = {
  transaction?: Store["purchase"]["activeTransactionMap"][string];
  eventImageMap: Record<string, string>;
  eventPartsMap: Record<string, EventPart[]>;
  bonusMap: Record<string, boolean> | null;
  isImageLoading: boolean;
};

type UseOrderListOutput = {
  orderList: ProductItem[];
};

export const useOrderList = (input: UseOrderListInput): UseOrderListOutput => {
  const {
    transaction,
    eventImageMap,
    eventPartsMap,
    bonusMap,
    isImageLoading,
  } = input;
  const { t } = useI18n();

  const orderList: ProductItem[] = useMemo(() => {
    if (!transaction || !transaction.products) return [];
    return transaction.products
      .map((product): ProductItem | null => {
        const type = getProductType(product);

        if (type === PRODUCT_TYPE.TICKET) {
          const eventParts = eventPartsMap[product.eid] ?? [];
          const matchingPart = eventParts.find(
            (part) =>
              part.name.includes(product.name) ||
              product.name.includes(part.name)
          );
          const eventDateTime =
            matchingPart?.openTime ?? product.releaseDateTime;

          return {
            productName: product.eventLongTitle,
            variationName: product.name,
            price: product.price_jpy,
            quantity: product.count,
            type,
            imageSrc: eventImageMap[product.eid] ?? "",
            isImageLoading,
            liveTicketInfo: {
              ...getReleaseDate(eventDateTime, t),
              status: getTicketStatus(eventDateTime),
              eventId: product.eid,
              ticketId: product.ref.id,
              class: getTicketClass(product.class),
              hasBonusMedia: bonusMap?.[product.ref.id] === true,
            },
          };
        }
        return {
          productName: product.eventLongTitle,
          variationName: product.name,
          price: product.price_jpy,
          quantity: product.count,
          type,
          imageSrc: product.thumbnail ?? "",
          isImageLoading: false,
        };
      })
      .filter((item): item is ProductItem => item !== null);
  }, [eventImageMap, eventPartsMap, t, transaction, bonusMap, isImageLoading]);

  return { orderList };
};
